import { keepPreviousData, queryOptions, useMutation, useQueryClient } from '@tanstack/react-query'
import type { AgentRequestInput, AskInput, DecisionInput, PendingAction } from '@/contract'
import type { UsagePeriod } from '@/contract'
import { panelApi, type FormsFilter } from './api'

/* Spec COG-005 — TanStack Query keys, queries and mutations (all under ['panel', ...]).
   "Live" = polling: lists every 10 s (the server caches them for 10 s), Ao Vivo every 5 s. */

const POLL_MS = 10_000
const LIVE_POLL_MS = 5_000

export const queryKeys = {
    all: ['panel'] as const,
    me: ['panel', 'me'] as const,
    askReply: (ticket: string) => ['panel', 'ask', ticket] as const,
    overview: ['panel', 'overview'] as const,
    agents: ['panel', 'agents'] as const,
    agentRole: (slug: string) => ['panel', 'agents', 'role', slug] as const,
    connections: ['panel', 'connections'] as const,
    pending: ['panel', 'pending'] as const,
    pendingDetail: (id: string) => ['panel', 'pending', 'detail', id] as const,
    forms: ['panel', 'forms'] as const,
    formsList: (filter: FormsFilter) => ['panel', 'forms', 'list', filter.status ?? '', filter.q?.trim() ?? '', filter.page ?? 1] as const,
    formsSummary: ['panel', 'forms', 'summary'] as const,
    live: ['panel', 'live'] as const,
    memory: ['panel', 'memory'] as const,
    pendingExtras: (id: string) => ['panel', 'pending', 'extras', id] as const,
    decisions: ['panel', 'decisions'] as const,
    liveExtras: ['panel', 'live', 'extras'] as const,
    liveFlows: ['panel', 'live', 'flows'] as const,
    liveFlow: (slug: string) => ['panel', 'live', 'flows', slug] as const,
    // Under ['panel', 'connections']: invalidating the connections (Testar) also refreshes health and detail
    connectionsHealth: ['panel', 'connections', 'health'] as const,
    connectionDetail: (slug: string) => ['panel', 'connections', 'detail', slug] as const,
    agentRequests: ['panel', 'agent-requests'] as const,
    // Under ['panel', 'agents']: Pausar/Retomar also refreshes the org chart
    agentsOrg: ['panel', 'agents', 'org'] as const,
    usage: (period: string) => ['panel', 'usage', period] as const,
}

/* No retry: the session gate decides right away (401 → login card). */
export const meQuery = queryOptions({ queryKey: queryKeys.me, queryFn: panelApi.me, staleTime: 60_000, retry: false })

export const overviewQuery = queryOptions({ queryKey: queryKeys.overview, queryFn: panelApi.overview, refetchInterval: POLL_MS })

export const agentsQuery = queryOptions({ queryKey: queryKeys.agents, queryFn: panelApi.agents, refetchInterval: POLL_MS })

/* The agent's function comes from its definition file and changes rarely (server cache: 1 h). */
export const agentRoleQuery = (slug: string) =>
    queryOptions({ queryKey: queryKeys.agentRole(slug), queryFn: () => panelApi.agentRole(slug), staleTime: 60 * 60_000 })

export const connectionsQuery = queryOptions({ queryKey: queryKeys.connections, queryFn: panelApi.connections, refetchInterval: POLL_MS })

/* ── Phase 2: Precisa de você ─────────────────────────────────────────────── */

export const pendingQuery = queryOptions({ queryKey: queryKeys.pending, queryFn: panelApi.pending, refetchInterval: POLL_MS })

export const pendingDetailQuery = (id: string) =>
    queryOptions({ queryKey: queryKeys.pendingDetail(id), queryFn: () => panelApi.pendingDetail(id) })

type DecideVars = { id: string; action: PendingAction } & DecisionInput

/** Approve/reject, return to the agent or close a pending item; refreshes the lists that show it (pending, Início, rail badge). */
export function useDecideMutation() {
    const client = useQueryClient()
    return useMutation({
        mutationFn: ({ id, action, note }: DecideVars) => panelApi.decide(id, action, note ? { note } : {}),
        onSuccess: () => {
            void client.invalidateQueries({ queryKey: queryKeys.pending })
            void client.invalidateQueries({ queryKey: queryKeys.overview })
            void client.invalidateQueries({ queryKey: queryKeys.forms })
            void client.invalidateQueries({ queryKey: queryKeys.decisions })
        },
    })
}

function useAgentToggle(call: (slug: string) => ReturnType<typeof panelApi.pauseAgent>) {
    const client = useQueryClient()
    return useMutation({
        mutationFn: (slug: string) => call(slug),
        onSuccess: () => {
            void client.invalidateQueries({ queryKey: queryKeys.agents })
            void client.invalidateQueries({ queryKey: queryKeys.overview })
            void client.invalidateQueries({ queryKey: queryKeys.live })
            void client.invalidateQueries({ queryKey: queryKeys.decisions })
        },
    })
}

export const usePauseAgentMutation = () => useAgentToggle(panelApi.pauseAgent)
export const useResumeAgentMutation = () => useAgentToggle(panelApi.resumeAgent)

/* ── Phase 3: Fichas and Ao Vivo ──────────────────────────────────────────── */

/** One page of forms (10 per page); keeps the previous page on screen while the next one loads. */
export const formsQuery = (filter: FormsFilter) =>
    queryOptions({
        queryKey: queryKeys.formsList(filter),
        queryFn: () => panelApi.forms(filter),
        placeholderData: keepPreviousData,
        refetchInterval: POLL_MS,
    })

export const formsSummaryQuery = queryOptions({ queryKey: queryKeys.formsSummary, queryFn: panelApi.formsSummary, refetchInterval: POLL_MS })

export const liveQuery = queryOptions({ queryKey: queryKeys.live, queryFn: panelApi.live, refetchInterval: LIVE_POLL_MS })

/* ── Phase 4: Memória and Pergunte ────────────────────────────────────────── */

export const memoryQuery = queryOptions({ queryKey: queryKeys.memory, queryFn: panelApi.memory, refetchInterval: 30_000 })

/** Ask the Memória assistant or an agent. Refreshes Memória (its "queries today" list). */
export function useAskMutation() {
    const client = useQueryClient()
    return useMutation({
        mutationFn: (input: AskInput) => panelApi.ask(input),
        onSuccess: () => void client.invalidateQueries({ queryKey: queryKeys.memory }),
    })
}

/** Phase 4b: a question sent to an agent as a task; polled until the reply arrives (the page stops after a while). */
export const askReplyQuery = (ticket: string) =>
    queryOptions({ queryKey: queryKeys.askReply(ticket), queryFn: () => panelApi.askReply(ticket), retry: false })

/* ── Phase 6: review and live extras ──────────────────────────────────────── */

/** Protocol, e-mail to the person in charge and history of one pending item. */
export const pendingExtrasQuery = (id: string) =>
    queryOptions({ queryKey: queryKeys.pendingExtras(id), queryFn: () => panelApi.pendingExtras(id), refetchInterval: POLL_MS })

/** "Suas decisões": the signed-in person's own last decisions (refreshed after each decision). */
export const decisionsQuery = queryOptions({ queryKey: queryKeys.decisions, queryFn: () => panelApi.decisions(10), refetchInterval: 30_000 })

/** Capture pipeline, memory answers today and the other agents today (server cache: 10 s). */
export const liveExtrasQuery = queryOptions({ queryKey: queryKeys.liveExtras, queryFn: panelApi.liveExtras, refetchInterval: POLL_MS })

/* ── Phase 6: system health and "Para liberar" ────────────────────────────── */

/** Health per system (sparkline, uptime 30d, access). Pushed live by useHealthStream (SSE); this polling every 30 s
    is the fallback the screens turn off while the stream is live. */
export const connectionsHealthQuery = queryOptions({ queryKey: queryKeys.connectionsHealth, queryFn: panelApi.connectionsHealth, refetchInterval: 30_000 })

/** One system: health, permissions, log and checklist. */
export const connectionDetailQuery = (slug: string) =>
    queryOptions({ queryKey: queryKeys.connectionDetail(slug), queryFn: () => panelApi.connectionDetail(slug), refetchInterval: 30_000 })

/** Add a step to "Para liberar" or tick/untick one; refreshes that system's detail. */
export function useChecklistMutation(slug: string) {
    const client = useQueryClient()
    return useMutation({
        mutationFn: (input: { text: string } | { id: string; done: boolean }) =>
            'text' in input ? panelApi.addChecklistStep(slug, input.text) : panelApi.setChecklistStep(slug, input.id, input.done),
        onSuccess: () => void client.invalidateQueries({ queryKey: queryKeys.connectionDetail(slug) }),
    })
}

/* ── Phase 6b: agent flows (Ao Vivo) ──────────────────────────────────────── */

/** Every agent's flow (subway lanes); polled like the rest of Ao Vivo. */
export const liveFlowsQuery = queryOptions({ queryKey: queryKeys.liveFlows, queryFn: panelApi.liveFlows, refetchInterval: LIVE_POLL_MS })

/** One agent's flow (big map). */
export const liveFlowQuery = (slug: string) =>
    queryOptions({ queryKey: queryKeys.liveFlow(slug), queryFn: () => panelApi.liveFlow(slug), refetchInterval: LIVE_POLL_MS })

/* ── Phase 7: "Pedir um novo agente" ──────────────────────────────────────── */

/** Requests for new agents (the panel never creates one; whoever runs the engine does). */
export const agentRequestsQuery = queryOptions({ queryKey: queryKeys.agentRequests, queryFn: panelApi.agentRequests, refetchInterval: 30_000 })

/** Records a request; refreshes the list. */
export function useRequestAgentMutation() {
    const client = useQueryClient()
    return useMutation({
        mutationFn: (input: AgentRequestInput) => panelApi.requestAgent(input),
        onSuccess: () => void client.invalidateQueries({ queryKey: queryKeys.agentRequests }),
    })
}

/* ── Phase 8: agents org chart ────────────────────────────────────────────── */

/** Who reports to whom (server cache: 30 s for the tree; status follows the agents list). */
export const agentsOrgQuery = queryOptions({ queryKey: queryKeys.agentsOrg, queryFn: panelApi.agentsOrg, refetchInterval: 30_000 })

/* ── Phase 8: AI usage ────────────────────────────────────────────────────── */

/** Tokens and estimated cost for one period (server cache: 60 s); keeps the previous period on screen while switching. */
export const usageQuery = (period: UsagePeriod) =>
    queryOptions({ queryKey: queryKeys.usage(period), queryFn: () => panelApi.usage(period), placeholderData: keepPreviousData, refetchInterval: 60_000 })
